import { useState, useCallback, useRef, useEffect } from "react";
import * as APIModels from "../../../models";
import Select, { ActionMeta } from "react-select";
import { CaseDataService } from "../../../services/SubmissionService";

type OptionType = { label: string; value: string };

export default function PartialCaseSearch({
    sequenceDetected,
    searchingAttorneyId,
    onSearchComplete,
    onCaseSelected,
}: {
    sequenceDetected: string;
    searchingAttorneyId: string | undefined;
    onSearchComplete: () => void | undefined;
    onCaseSelected: (result: APIModels.Submission) => void;
}) {
    const [searchResults, setSearchResults] = useState(
        [] as APIModels.Submission[]
    );
    const [selectedCase, setSelectedCase] = useState(
        null as APIModels.Submission | null
    );
    const [sequenceNumber, setSequenceNumber] = useState(
        sequenceDetected || ""
    );
    const [searching, setSearching] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showTooltip, setShowTooltip] = useState(false);

    const caseSearchClick = useCallback(async () => {
        console.log(sequenceNumber);
        if (sequenceNumber && sequenceNumber.length > 0) {
            setSearching(true);

            CaseDataService.find_original_new_suit({
                caseNumber: sequenceNumber,
                attorneyId: searchingAttorneyId,
            }).then(
                (resp) => {
                    if (onSearchComplete) {
                        onSearchComplete();
                    }
                    setSearching(false);
                    if (resp.successful == true) {
                        if (resp.data) setSearchResults(resp.data);
                        if (!resp.data || resp.data.length === 0) {
                            setErrorMessage("No matching cases found.");
                        } else {
                            setErrorMessage("");
                        }
                    }
                },
                (error) => {
                    setSearching(false);
                    console.log(error);
                    setErrorMessage("An error occurred while searching.");
                }
            );
        }
    }, [sequenceNumber]);

    const onCaseChange = (
        value: OptionType | null,
        actionMeta: ActionMeta<OptionType>
    ) => {
        const matchingCases = searchResults.filter(
            (v) => v.caseData?.ucn === value!.value
        );
        if (matchingCases.length > 0) {
            setSelectedCase(matchingCases[0]);
            onCaseSelected(matchingCases[0]);
        }
    };

    useEffect(() => {
        if (searchResults.length === 1) {
            setSelectedCase(searchResults[0]);
            onCaseSelected(searchResults[0]);
        }
    }, [searchResults]);

    const filterOption = (option: OptionType, inputValue: string): boolean =>
        (
            option.label
                .toString()
                .toLowerCase()
                .match(inputValue.toLowerCase()) || []
        ).length > 0;

    return (
        <>
            <div className="mb-3">
                <label htmlFor="txtSeqNum" className="form-label">
                    Sequence Number
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="txtSeqNum"
                    placeholder="Sequence Number"
                    value={sequenceNumber}
                    onChange={(event) => {
                        console.log(event.currentTarget.value);
                        setSequenceNumber(event.currentTarget.value);
                    }}
                />
                <div className="invalid-feedback">
                    Please provide a valid sequence number.
                </div>
            </div>
            <div className="mb-3">
                <img src="/assets/images/sequence-number.png" />
            </div>
            <div className="mb-3">
                {searching ? (
                    <button
                        className="btn btn-primary"
                        type="button"
                        disabled={true}
                    >
                        <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        Searching...
                    </button>
                ) : (
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={caseSearchClick}
                    >
                        Search
                    </button>
                )}
                {errorMessage && (
                    <div className="text-danger mt-2">{errorMessage}</div>
                )}
            </div>
            {selectedCase && (
                <>
                    <div className="mb-3">
                        <label className="form-label">UCN</label>
                        <div
                            style={{
                                display: "block",
                                position: "relative",
                                cursor: "pointer",
                            }}
                            title="Hover to see parties"
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                        >
                            <a
                                style={{
                                    display: "block",
                                    textDecoration: "underline",
                                    color: "#007bff",
                                }}
                            >
                                {selectedCase.caseData?.ucn}
                            </a>

                            {/* Parties tooltip on hover */}
                            {showTooltip && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "100%",
                                        left: "0",
                                        zIndex: 1000,
                                        backgroundColor: "white",
                                        border: "1px solid #dee2e6",
                                        borderRadius: "0.25rem",
                                        padding: "0.75rem",
                                        marginTop: "0.25rem",
                                        minWidth: "300px",
                                        maxWidth: "500px",
                                        boxShadow:
                                            "0 0.5rem 1rem rgba(0,0,0,0.15)",
                                    }}
                                >
                                    <strong className="d-block mb-2">
                                        Parties:
                                    </strong>
                                    {selectedCase.parties &&
                                    selectedCase.parties.length > 0 ? (
                                        <ul className="list-unstyled mb-0">
                                            {selectedCase.parties.map(
                                                (party, index) => (
                                                    <li
                                                        key={index}
                                                        className="mb-1"
                                                    >
                                                        <span className="badge bg-secondary me-2">
                                                            {party.partyType ||
                                                                "Party"}
                                                        </span>
                                                        {party.firstName &&
                                                        party.lastName
                                                            ? `${party.firstName} ${party.lastName}`
                                                            : party.name ||
                                                              "Unknown Party"}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    ) : (
                                        <span className="text-muted">
                                            No parties found
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Case Title</label>
                        <div>{selectedCase.caseData?.caseTitle}</div>
                    </div>
                </>
            )}
            <div className="mb-3">
                <label htmlFor="selCase" className="form-label">
                    Matching Cases
                </label>
                <Select
                    id="selCase"
                    name="selCase"
                    filterOption={filterOption}
                    options={searchResults.map((v) => {
                        return {
                            label: v.caseData?.caseTitle ?? "",
                            value: v.caseData?.ucn ?? "",
                        };
                    })}
                    onChange={onCaseChange}
                />
            </div>
        </>
    );
}
